/* eslint-disable no-bitwise */
import axios from "axios";
import FileSaver from "file-saver";
import Jsona from "jsona";
import { useDrupalSettings } from "../composables/useDrupalSettings";

const { config } = useDrupalSettings();

const dataFormatter = new Jsona();

const hashPath = (path) =>
  path
    .split("")
    .map((v) => v.charCodeAt(0))
    .reduce((a, v) => (a + ((a << 7) + (a << 3))) ^ v)
    .toString(16);

const readAsArrayBuffer = (file) => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new DOMException("Problem parsing input file."));
    };
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsArrayBuffer(file);
  });
};

const defaults = {
  headers: {
    Authorization: "Bearer " + config.jwt,
    "X-CSRF-Token": config.csrfToken,
    CrossDomain: true,
  },
};

const jsonRequest = axios.create({
  baseURL: config.baseUrl + "/jsonapi",
  headers: {
    ...defaults.headers,
    "Content-Type": "application/vnd.api+json",
  },
});

jsonRequest.interceptors.response.use((response) => {
  if (response.data?.data) {
    const deserialized = dataFormatter.deserialize(response.data);
    // Add a deserialized key to response.data
    return { ...response, data: { ...response.data, deserialized } };
  }
  return response;
});

const restRequest = axios.create({
  baseURL: config.restUrl,
  headers: defaults.headers,
});

const baseRequest = axios.create({
  baseURL: config.baseUrl,
  headers: defaults.headers,
});

const getErrorResponseData = (error) => {
  let data = null;
  const errorResponseType = error.response?.headers?.["content-type"];
  if (
    ["application/json", "application/vnd.api+json"].includes(errorResponseType)
  ) {
    data = error.response?.data ? error.response?.data : error.response;
  } else if (typeof error.response === "undefined") {
    data = { status: 0, statusText: "No response" };
  }
  return data;
};

const logErrors = (error) => {
  // Only log for specified errors.
  const logfor = [403, 404, 422, 500];
  if (
    typeof error.response?.status !== "undefined" &&
    logfor.indexOf(error.response?.status) === -1
  ) {
    return;
  }
  // Prevent duplicates/recursion
  if (typeof error.logSent !== "undefined" && error.logSent) {
    return;
  }
  error.logSent = true;

  // get response data if we can
  const response = getErrorResponseData(error);
  // eslint-disable-next-line no-console
  console.warn({ error });
  const jsonMessage = {
    message: error.message,
    url: error.config?.url,
    method: error.config?.method,
    data: error.config?.data,
    response: response,
    status: error.response?.status,
    statusText: error.response?.statusText,
    module: config?.module,
  };
  const data = {
    message: JSON.stringify(jsonMessage),
    channel: jsonMessage.module,
  };
  const options = {
    headers: { "Content-Type": "application/json" },
  };
  return restRequest.post("/log?_format=json", data, options);
};

// Add a response interceptor
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    logErrors(error);
    return Promise.reject(error);
  },
);

const fileUtils = {
  sanitizeFilename(name) {
    // Code partially inspired by
    // https://github.com/parshap/node-sanitize-filename
    // and adapted under the WTFPL.
    // Illegal Characters on Various Operating Systems:
    // / ? < > \ : * | "
    const illegalChars = /[/?<>\\:*|"]/gu;
    // File names should not have leading "." characters.
    const leadingDot = /^\.+/;
    // ASCII control characters from
    // https://en.wikipedia.org/wiki/C0_and_C1_control_codes
    // eslint-disable-next-line no-control-regex
    const controlChars = /[\x00-\x1f\x7F-\x9f]/gu;
    const newName = name
      .replace(illegalChars, "_")
      .replace(leadingDot, "_")
      .replace(controlChars, "_");
    const dotPos = newName.lastIndexOf(".");
    let baseName = newName;
    let extension = "";
    if (dotPos !== -1) {
      baseName = newName.substring(0, dotPos);
      extension = newName.substring(dotPos);
    }
    // Drupal file URI is limited to 255 chars (including scheme and path)
    // so truncate at 200.
    return baseName.substring(0, 200 - extension.length) + extension;
  },
};

const jsonClient = {
  controllers: {},

  addAbortController(path) {
    const pathId = hashPath(path);
    this.controllers[pathId] =
      this.controllers[pathId] || new AbortController();
    return pathId;
  },

  cancelRequest(path) {
    const pathId = hashPath(path);
    this.controllers[pathId]?.abort();
    this.controllers[pathId] = null;
    return this;
  },

  async get({ path, queries }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const pathId = this.addAbortController(path);
      const response = await jsonRequest.get(path, {
        params: queries,
        signal: this.controllers[pathId].signal,
      });
      // @todo: Remove unused spread for RAFT-4851
      return { ...response.data, ...response.deserialized };
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async patch({ path, queries, data, returnResponse = false }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const response = await jsonRequest.patch(
        path,
        { data },
        { params: queries },
      );
      return returnResponse ? response : response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async post({ path, queries, data }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const pathId = this.addAbortController(path);
      const response = await jsonRequest.post(
        path,
        { data },
        {
          params: queries,
          signal: this.controllers[pathId].signal,
        },
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async delete({ path }) {
    return jsonRequest.delete(path);
  },
  async postFile({ path, queries, file, controller }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    const headers = {
      ...defaults.headers,
      "Content-Disposition": 'file; filename="' + file.name + '"',
      "Content-Type": "application/octet-stream",
    };
    const readerResult = await readAsArrayBuffer(file);
    try {
      const response = await jsonRequest.post(path, readerResult, {
        params: queries,
        controller,
        headers,
      });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async postFilesToField({
    entityType,
    entityBundle,
    fieldName,
    queries,
    files,
  }) {
    // @see https://www.drupal.org/node/3024331 for information about how
    // to upload and attach files in JSON:API.
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for queries with ${queries}`,
      );
    }
    const path = `${entityType}/${entityBundle}/${fieldName}`;
    const promises = [];
    files.forEach((file) => {
      const headers = {
        ...defaults.headers,
        "Content-Disposition":
          'file; filename="' + fileUtils.sanitizeFilename(file.name) + '"',
        "Content-Type": "application/octet-stream",
      };
      const response = jsonRequest.post(path, file, {
        params: queries,
        headers,
      });
      promises.push(response);
    });
    const responses = [];
    await Promise.allSettled(promises).then((results) => {
      results.forEach((result) => {
        if (result.status === "fulfilled") {
          responses.push(result.value.data);
        } else {
          responses.push(result.reason);
        }
      });
    });
    return responses;
  },
  async getAsPost({ path, queries }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const pathId = this.addAbortController(path);
      const options = {
        headers: {
          ...defaults.headers,
          "Content-Type": "application/x-www-form-urlencoded",
          "X-HTTP-Method-Override": "GET",
        },
        signal: this.controllers[pathId].signal,
      };
      const queryParams = Object.keys(queries)
        .map((key) => key + "=" + encodeURIComponent(queries[key]))
        .join("&");

      const response = await jsonRequest.post(path, queryParams, options);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getAllResults({ path, queries }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      let response = await jsonRequest.get(path, { params: queries });
      let allItems = response?.data?.deserialized;
      while (response?.data?.links?.hasOwnProperty("next")) {
        path = response?.data?.links?.next?.href?.split("jsonapi/");
        // eslint-disable-next-line no-await-in-loop
        response = await jsonRequest.get(path[1]);
        allItems = allItems?.concat(response?.data?.deserialized);
      }
      return allItems;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async formatResultsAsCsv(jsonApiResults, fileName, headers, fieldFormatter) {
    try {
      const csvString = [
        headers,
        ...jsonApiResults.map((row) =>
          [...fieldFormatter(row)].map(
            (str) => `"${str.toString().replace(/"/g, '"')}"`,
          ),
        ),
      ]
        .map((e) => e.join(","))
        .join("\n");
      const blob = new Blob([csvString], { type: "text/csv; charset=utf-8" });
      FileSaver.saveAs(blob, fileName);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

const restClient = {
  controllers: {},

  addAbortController(path) {
    const pathId = hashPath(path);
    this.controllers[pathId] =
      this.controllers[pathId] || new AbortController();
    return pathId;
  },

  cancelRequest(path) {
    const pathId = hashPath(path);
    this.controllers[pathId]?.abort();
    this.controllers[pathId] = null;
    return this;
  },

  async get({ path, queries }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    queries._format = "json";
    try {
      const pathId = this.addAbortController(path);
      const response = await restRequest.get(path, {
        params: queries,
        signal: this.controllers[pathId].signal,
      });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async head({ path, queries }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(`missing query object for ${path}`);
    }
    queries._format = "json";
    try {
      const response = await restRequest.head(path, { params: queries });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async patch({ path, queries, data }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const response = await restRequest.patch(path, data, { params: queries });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async post({ path, queries, data }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const response = await restRequest.post(path, data, { params: queries });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async downloadFile(path) {
    const response = await this.downloadFileFullResponse(path);
    if (response.hasOwnProperty("data")) {
      return response.data;
    }
    return response;
  },
  async downloadFileFullResponse(path) {
    const options = { responseType: "blob", headers: defaults.headers };
    try {
      return await baseRequest.get(path, options);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async downloadFileStream({
    path,
    filename,
    queries,
    extraOptions = {},
    useResponseFilename = false,
  }) {
    const format = filename.split(".").pop() || "csv";
    const options = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-HTTP-Method-Override": "GET",
      },
      params: {
        _format: format,
      },
    };
    Object.keys(extraOptions).forEach((key) => {
      options[key] = extraOptions[key];
    });
    const body = new FormData();
    Object.keys(queries).forEach((key) => {
      body.set(key, queries[key]);
    });
    let downloadFileName = null;
    try {
      const response = await baseRequest.post(path, body, options);
      const type =
        response.headers["content-type"] || "text/csv; charset=utf-8";
      const blob = new Blob([response.data], { type });
      const disposition = response.headers["content-disposition"];
      if (useResponseFilename && disposition) {
        const re = /filename="(?<filename>[^"]+)"/i;
        downloadFileName = re.exec(disposition)?.groups?.filename;
      }
      const filenameParts = [];
      if (!downloadFileName) {
        const date = new Date();
        const pad = (num) => ("0" + num).slice(-2);
        filenameParts.push(date.getFullYear());
        filenameParts.push(pad(date.getMonth() + 1));
        filenameParts.push(pad(date.getDate()));
        filenameParts.push(filename);
        downloadFileName = filenameParts.join("-");
      }
      return FileSaver.saveAs(blob, downloadFileName);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

const b3Request = axios.create({
  baseURL: config.b3baseUrl,
  headers: {
    ...defaults.headers,
    // 'Content-Type': 'application/x-www-form-urlencoded',
    "Content-Type": "application/json",
  },
});
const b3RestClient = {
  async get({ path, queries }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    queries._format = "json";
    try {
      const response = await b3Request.get(path, { params: queries });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async post({ path, queries, data }) {
    if (typeof queries !== "object" || !queries) {
      throw new Error(
        `missing query object or incorrect type for ${path} with ${queries}`,
      );
    }
    try {
      const response = await b3Request.post(path, data, { params: queries });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

export { jsonClient, restClient, b3RestClient, fileUtils };
